/** Device configuration settings stored in IndexedDB */

import { openDatabase } from '@tunasong/indexeddb-storage'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Event emitter for cross-component updates
const dbEventEmitter = {
  listeners: new Set<(key: string, value: unknown) => void>(),
  subscribe(callback: (name: string, value: unknown) => void) {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  },
  emit<T>(key: string, value: T) {
    for (const callback of this.listeners) {
      callback(key, value)
    }
  },
}

/**
 * Store a named state on a device IndexedDB database.
 *
 * The value is loaded async from IndexedDB, so if you need to wait for the value to be loaded,
 * set the `defaultValue` and wait to render until val is available.
 */
export const useDeviceState = <T>(name: string, defaultValue?: T, nameSpace = 'device') => {
  const db = useMemo(() => openDatabase<T>(nameSpace), [nameSpace])
  const [val, setVal] = useState<T | undefined>(defaultValue)
  const [ready, setReady] = useState(false)

  const lastName = useRef<string>(undefined)
  const defaultValueRef = useRef(defaultValue)
  defaultValueRef.current = defaultValue

  useEffect(() => {
    const unsubscribe = dbEventEmitter.subscribe((key, value) => {
      if (key !== name) {
        return
      }
      setVal(value as T)
    })

    return () => {
      unsubscribe()
    }
  }, [name])
  useEffect(() => {
    if (!db) {
      return
    }

    // Load the initial value
    void db.get(name).then(val => {
      setVal(val ?? defaultValueRef.current)
      lastName.current = name
      setReady(true)
    })
  }, [db, name])

  const set = useCallback(
    (value: T) => {
      setVal(value)
      void db?.set(name, value)
      dbEventEmitter.emit(name, value)
    },
    [db, name]
  )

  return [val, set, ready] as [T | undefined, typeof set, boolean]
}
